import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { logoutFromCognito, subscribeAuthLogout } from '@/lib/auth'
import { safeNextPath } from '@/lib/safe-next-path'
import { useAuthStore } from '@/store/auth'
import { WelcomePage } from '@/pages/WelcomePage'
import { SignUpPage } from '@/pages/SignUpPage'
import { SignInPage } from '@/pages/SignInPage'
import { VerifyEmailPage } from '@/pages/VerifyEmailPage'
import { HomePage } from '@/pages/HomePage'
import { LiveAccountPage } from '@/pages/LiveAccountPage'
import { DemoAccountPage } from '@/pages/DemoAccountPage'
import { PbkFormsPage } from '@/pages/PbkFormsPage'
import { FundsPage } from '@/pages/FundsPage'
import { CddsWalkPage } from '@/pages/CddsWalkPage'
import { FundingPage } from '@/pages/FundingPage'
import { AgreementFormPage } from '@/pages/AgreementFormPage'
import { RegolDocumentTitle } from '@/components/RegolDocumentTitle'

function RouteLoading({ shell }: { shell: 'prod-shell' | 'prod-auth' }) {
  const { t } = useTranslation()
  return (
    <div className={`${shell} flex min-h-screen items-center justify-center`} role="status">
      {t('common.loading')}
    </div>
  )
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore()
  const location = useLocation()

  if (isLoading) {
    return (
      <RouteLoading shell="prod-shell" />
    )
  }

  if (!isAuthenticated) {
    const next = `${location.pathname}${location.search}`
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />
  }

  return <>{children}</>
}

function PublicOnlyRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore()
  const location = useLocation()

  if (isLoading) {
    return (
      <RouteLoading shell="prod-auth" />
    )
  }

  if (isAuthenticated) {
    const next = safeNextPath(new URLSearchParams(location.search).get('next'))
    return <Navigate to={next ?? '/dashboard'} replace />
  }

  return <>{children}</>
}

function UnknownRoute() {
  const { isAuthenticated, isLoading } = useAuthStore()

  if (isLoading) {
    return (
      <RouteLoading shell="prod-shell" />
    )
  }

  return <Navigate to={isAuthenticated ? '/dashboard' : '/'} replace />
}

export default function App() {
  useEffect(() => subscribeAuthLogout(() => void logoutFromCognito(false)), [])

  return (
    <>
      <RegolDocumentTitle />
      <Routes>
      <Route path="/" element={<PublicOnlyRoute><WelcomePage /></PublicOnlyRoute>} />
      <Route
        path="/sign-up"
        element={
          <PublicOnlyRoute>
            <SignUpPage />
          </PublicOnlyRoute>
        }
      />
      <Route
        path="/login"
        element={
          <PublicOnlyRoute>
            <SignInPage />
          </PublicOnlyRoute>
        }
      />
      <Route path="/sign-in" element={<Navigate to="/login" replace />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />

      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        }
      />
      <Route path="/home" element={<Navigate to="/dashboard" replace />} />
      <Route
        path="/register/data"
        element={
          <ProtectedRoute>
            <AgreementFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/register/cdds/:code?"
        element={
          <ProtectedRoute>
            <CddsWalkPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/funding"
        element={
          <ProtectedRoute>
            <FundingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/agreement/:id"
        element={
          <ProtectedRoute>
            <AgreementFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/live-account"
        element={
          <ProtectedRoute>
            <LiveAccountPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/demo-account"
        element={
          <ProtectedRoute>
            <DemoAccountPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pbk-forms"
        element={
          <ProtectedRoute>
            <PbkFormsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/funds"
        element={
          <ProtectedRoute>
            <FundsPage />
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<UnknownRoute />} />
    </Routes>
    </>
  )
}
